import {
   type CountryCode,
   type LanguageCode,
   ShopifyStorefrontProvider,
} from '@ifixit/shopify-storefront-client';
import initializeComponent from 'Shared/react-initialize';
import { requireAuth } from 'Shared/require-auth';
import { bindLightboxImageClicks } from 'Shared/lightbox_image_clicks';
import { Tabs, TabsContent } from '@ifixit/ui/components/tabs';
import { useState, type Dispatch, type SetStateAction } from 'react';
import { UserCard } from './Sidebar/user_card';
import { UserTabs, userTabTriggerId } from './user_tabs';
import { UserView } from './user_view';
import type { UserSummaryType } from './Sidebar/user_card';
import { useProfileTab } from './use_profile_tab';
import { Skeleton } from '@ifixit/ui/components/skeleton';
import { ButtonStyles } from '@ifixit/ui/components/button';
import { _js } from '@ifixit/utils/localize';

export type SidebarDataType = {
   userCardData: UserSummaryType;
};

const UserPage = ({
   userid,
   viewData,
   sidebarData,
   activeTab,
   unreadNotificationCount,
   shopifyStore,
}: {
   userid: number;
   viewData: any[];
   editUrl?: string;
   usersUrl: string;
   sidebarData: SidebarDataType;
   activeTab: string;
   unreadNotificationCount: number;
   shopifyStore: {
      currencyCode: string;
      country: CountryCode;
      language: LanguageCode;
      storeCode: string;
      shopDomain: string;
      storefrontAccessToken: string;
   };
}) => {
   const [currentTab, setCurrentTab] = useState(activeTab);
   const [unreadNotifCount, setUnreadNotifCount] = useState(unreadNotificationCount);
   const tabIcons = {
      'About': 'address-card',
      'Devices': 'grid-2',
      'Contributions': 'files',
      'Activity': 'list-timeline',
      'Reputation': 'certificate',
      'Notifications': 'bell',
      'Orders': 'store',
      'Subscriptions': 'calendar-check',
   };

   const layoutClassName =
      'flex max-md:flex-col justify-between gap-8 md:gap-12 mx-auto max-w-[1024px] mt-4 md:mt-16';
   // Routes set activeTab without the gating getViewTabs() applies (/User/Orders
   // without feature-cart, /User/Contributions on meta), so the trigger this
   // panel names is not guaranteed to render.
   const hasTabTrigger = viewData.some((tab: any) => tab.name === currentTab);
   const profileDisplay = (
      <UserProfileDisplay
         userid={userid}
         viewData={viewData}
         currentTab={currentTab}
         setUnreadNotifCount={setUnreadNotifCount}
      />
   );

   if (App.isOnMobileApp) {
      return (
         <ShopifyStorefrontProvider {...shopifyStore}>
            <div className={layoutClassName}>
               <div className="md:flex-[656px]">{profileDisplay}</div>
            </div>
         </ShopifyStorefrontProvider>
      );
   }

   return (
      <ShopifyStorefrontProvider {...shopifyStore}>
         <Tabs value={currentTab} className={layoutClassName}>
            <div className="flex flex-col gap-4 min-lg:w-[320px]">
               <UserCard {...sidebarData.userCardData} />
               <UserTabs
                  viewTabs={viewData}
                  notifBadge={unreadNotifCount}
                  setCurrentTab={setCurrentTab}
                  tabIcons={tabIcons}
               />
            </div>
            <TabsContent
               value={currentTab}
               aria-labelledby={hasTabTrigger ? userTabTriggerId(currentTab) : undefined}
               className="mt-0 md:flex-[656px]"
            >
               {profileDisplay}
            </TabsContent>
         </Tabs>
      </ShopifyStorefrontProvider>
   );
};

const UserProfileDisplay = ({
   userid,
   viewData,
   currentTab,
   setUnreadNotifCount,
}: {
   userid: number;
   viewData: any[];
   currentTab: string;
   setUnreadNotifCount: Dispatch<SetStateAction<number>>;
}) => {
   const selectedTab = viewData.find((tab: any) => tab.name === currentTab);
   const { data, isPending, isError } = useProfileTab(userid, currentTab, selectedTab);

   // Routes set activeTab without the gating the server applies, so the
   // current tab is not guaranteed to be one this profile has.
   if (!selectedTab) {
      return null;
   }
   if (selectedTab.requiresLogin) {
      return <TabLoginPrompt />;
   }
   if (isError) {
      return <div>{_js('Something went wrong.')}</div>;
   }
   if (isPending) {
      return <TabSkeleton />;
   }
   return (
      <UserView
         userid={userid}
         currentTab={{ ...selectedTab, data }}
         setUnreadNotifCount={setUnreadNotifCount}
      />
   );
};

const TabLoginPrompt = () => {
   // The same sentence lives in UserProfileTabData::loginRequiredMessage(),
   // which is what the page and the API answer with. `_js()` reads the same
   // gettext catalog `___()` fills, so the two have to stay character for
   // character identical or this one falls back to untranslated English.
   // requiresLogin comes from the server render, so only a reload shows the tab.
   const logIn = () => requireAuth({ reload: true, onAuthorize: () => {} });
   return (
      <div className="flex flex-col items-start gap-4">
         <p className="m-0">{_js("Log in to see this part of someone's profile.")}</p>
         <button type="button" className={ButtonStyles({ variant: 'white' })} onClick={logIn}>
            {_js('Log in')}
         </button>
      </div>
   );
};

const TabSkeleton = () => (
   <div className="flex flex-col gap-4">
      <Skeleton className="h-8 w-48" />
      <Skeleton className="h-24 w-full rounded-2xl" />
      <Skeleton className="h-24 w-full rounded-2xl" />
   </div>
);

// The About tab injects the profile's rendered wiki text, images and all.
bindLightboxImageClicks();

initializeComponent('UserPage', UserPage);
